import React, { useState, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useDebounce } from "react-use";
import DeckGL from "@deck.gl/react";
import { GeoJsonLayer, BitmapLayer } from "@deck.gl/layers";
import { TileLayer, _WMSLayer as WMSLayer } from "@deck.gl/geo-layers";
import proj4 from "proj4";
import { PathStyleExtension } from "@deck.gl/extensions";
import { MapContext, StaticMap, ScaleControl, Popup } from "react-map-gl";
import {
  EditableGeoJsonLayer,
  ViewMode,
} from "@deck.gl-community/editable-layers";

import axios from "axios";
import { nanoid } from "nanoid";

import Box from "@mui/material/Box";

import Basemap from "./Basemap";
import ChangeView from "./ChangeView";
import Home from "./Home";
import OverlaySelection from "./OverlaySelection";
import PolygonTool from "./PolygonTool";
import Tooltip from "./Tooltip";

import { getDerivedDataState, getNewLayersAsync } from "../../utils/map";
import { getData } from "../../store/dataSlice";
import {
  aoiActions,
  overlayActions,
  layerActions,
  polygonActions,
} from "../../store";

import { ViewStateContext } from "../UrlParser/UrlParser";

import { staticLayers } from "../../config/config";
import { aoiCategories } from "../../config/config.aoi";
import { DEFAULT_FILL_COLOR } from "../../config/config.overlays";
import DataStepperButton from "../UI/DataStepperButton";

let tooltipLocked = false;

function Map() {
  const dispatch = useDispatch();
  const aoiState = useSelector((state) => state.aoi);
  const dataState = useSelector((state) => state.data);
  const siteState = dataState.sites;
  const mapState = useSelector((state) => state.map);
  const overlayState = useSelector((state) => state.overlay);
  const layerState = useSelector((state) => state.layers);

  // define portions of layerState for which map layers should be redrawn
  const activeLayers = Object.keys(layerState).filter(
    (layer) => layerState[layer].showLayer
  );
  const colorValues = Object.values(layerState).map((l) => l.colorValue);
  const activeFilters = Object.values(layerState)
    .map((l) => l.activeFilters)
    .filter((f) => f);
  const filteredSitesData = useSelector((state) => state.data["sites"]?.filteredData);
  const polygonState = useSelector((state) => state.polygon);

  const { viewState, setViewState } = useContext(ViewStateContext);

  const [layers, setLayers] = useState([]);
  const [mode, setMode] = useState(() => ViewMode);

  const [overlayData, setOverlayData] = useState({});
  const [polygonLayerVisibility, setPolygonLayerVisibility] = useState(true);
  const [polygonToolActive, setPolygonToolActive] = useState(false);
  const [staticLayersFetched, setStaticLayersFetched] = useState(false);
  // localViewState stores a copy of viewState locally and only updates viewState
  //   after 500ms without user interaction to reduce unnecessary
  //    operations that propagate from this state change (e.g. setting url info)
  // see https://github.com/visgl/deck.gl/issues/3933
  const [localViewState, setLocalViewState] = useState(viewState);
  const [tooltipInfo, setTooltipInfo] = useState({
    tooltipText: null,
    lat: null,
    lon: null,
  });

  const derivedDataState = getDerivedDataState(dataState);

  useDebounce(
    () => {
      setViewState(localViewState);
    },
    500,
    [localViewState]
  );

  // when <ChangeView> updates viewState, set it locally to zoom to AOI
  useEffect(() => {
    setLocalViewState(viewState);
  }, [viewState]);

  // set flag once all static layers are fetched to reload map
  useEffect(() => {
    if (
      staticLayers
        .map((dataId) => dataState[dataId].fetched)
        .every((fetched) => fetched)
    ) {
      setStaticLayersFetched(true);
    }
  }, [dataState]);

  function handleOverlayPolygonClick({ layer, object }) {
    setTooltipInfo({
      tooltipText: null,
      lat: null,
      lon: null,
      type: null,
    });
    dispatch(
      aoiActions.addSelection({
        catId: aoiState.overlay,
        id: object.id,
      })
    );
  }

  // set tooltip to AOI name on hover
  const handleOverlayPolygonHover = (e) => {
    tooltipLocked = false;
    if (e.object !== undefined) {
      setTooltipInfo({
        tooltipText: [{ value: e.object.properties.name, type: "text-header" }],
        lat: e.coordinate[1],
        lon: e.coordinate[0],
        type: "aoi",
      });
    } else {
      setTooltipInfo({
        tooltipText: null,
        lat: null,
        lon: null,
      });
    }
  };

  const handleMapEvent = (info, event) => {
    if (event.type === "pointermove") {
      if (!tooltipLocked && !info.object) {
        setTooltipInfo({
          tooltipText: null,
          layerId: null,
          siteId: null,
          type: null,
          lat: null,
          lon: null,
        });
      }
    }
    if (event.type === "click" && !info.object) {
      tooltipLocked = false;
    }

    // polygon tool
    // normal, single click
    if (event.tapCount === 1) {
      if (mode !== ViewMode) {
        // don't change selection while editing
        return;
      }
      let newSelectedFeatureIndexes = [];
      if (
        info.object &&
        info.layer &&
        info.layer.id === "EditableGeoJsonLayer"
      ) {
        if (polygonState.selectedFeatureIndexes.includes(info.index)) {
          newSelectedFeatureIndexes =
            polygonState.selectedFeatureIndexes.filter((i) => i !== info.index);
        } else {
          newSelectedFeatureIndexes = [
            ...polygonState.selectedFeatureIndexes,
            info.index,
          ];
        }
      }
      dispatch(
        polygonActions.setSelectedFeatureIndexes(newSelectedFeatureIndexes)
      );
    }
  };

  // handles hover and click events. type parameter gives easy access to event type
  const handlePointLayerEvent = (info, event, id, eventType) => {
    if (info.object && !tooltipLocked) {
      // hovertext stores one object per tooltip field
      //  each object has the following keys:
      //    "label" - tooltip label
      //    "value" - tooltip value
      //    "type" - content type: text, external link, image, etc
      //    "group" - defines grouping of fields in tooltip
      const hoverText = [];

      layerState[id].tooltipFields.forEach((tooltipField) => {
        switch (tooltipField.type) {
          case "title": {
            hoverText.push({
              value: info.object.properties[tooltipField.fieldName],
              type: tooltipField.type,
              section: tooltipField.section,
            });
            break;
          }
          default:
            break;
        }
      });
      let hasSiteId = true;
      let siteId = info.object.properties.hasOwnProperty(
        layerState[id].siteNumberField
      )
        ? info.object.properties[layerState[id].siteNumberField]
        : null;

      // if site doesn't have an id defined in one of its properties default to the id of the field
      if (!siteId) {
        siteId = info.object.id;
        hasSiteId = false;
      }

      setTooltipInfo({
        tooltipText: hoverText,
        layerId: id,
        siteId,
        hasSiteId,
        type: eventType,
        lat: info.object.geometry.coordinates[1],
        lon: info.object.geometry.coordinates[0],
        info,
      });
    }
    if (event.type === "click" && info.layer) {
      tooltipLocked = !tooltipLocked;
    }
  };

  useEffect(() => {
    Promise.all(
      getNewLayersAsync({
        aoiState,
        dataState,
        dispatch,
        getData,
        filteredSitesData,
        handlePointLayerEvent,
        layers: layerState,
        layerActions: layerActions,
      })
    ).then((resolvedLayers) => {
      // initialize layers as empty array
      let newLayers = [];

      // basemap as tileLayer
      if (mapState.basemap.type === "tileLayer") {
        newLayers.push(
          new TileLayer({
            data: mapState.basemap.src,
            type: "raster",
            minZoom: mapState.basemap.minZoom,
            maxZoom: mapState.basemap.maxZoom,
            tileSize: mapState.basemap.tileSize,
            renderSubLayers: (props) => {
              const {
                bbox: { west, south, east, north },
              } = props.tile;

              return new BitmapLayer(props, {
                data: null,
                image: props.data,
                bounds: [west, south, east, north],
              });
            },
          })
        );
      }

      // If AOI is selected, add AOI polygon to layers
      if (aoiState.selection.category && aoiState.selection.id) {
        newLayers.push(
          new GeoJsonLayer({
            id: "aoiSelection",
            data: dataState[
              aoiCategories.find(
                (aoiCat) => aoiCat.id === aoiState.selection.category
              ).data
            ].data.features.find((feat) => feat.id === aoiState.selection.id),
            filled: false,
            // getFillColor: [160, 160, 180, 200],
            getLineColor: [160, 160, 180, 255],
            getLineWidth: 3,
            lineWidthMinPixels: 3,
            lineWidthUnits: "pixels",
            onClick: handleOverlayPolygonClick,
            pickable: true,
            getDashArray: [3, 2],
            dashJustified: true,
            dashGapPickable: true,
            extensions: [new PathStyleExtension({ dash: true })],
          })
        );
      }

      // Add static layers
      staticLayers.forEach((layerId) =>
        newLayers.push(
          new GeoJsonLayer({
            id: `${layerId}-static`,
            data: dataState[layerId].data,
            filled: false,
            getLineColor: [160, 160, 180, 255],
            getLineWidth: 2,
            lineWidthMinPixels: 2,
            lineWidthUnits: "pixels",
            onClick: handleOverlayPolygonClick,
            pickable: false,
            getDashArray: [3, 2],
            dashJustified: true,
            dashGapPickable: true,
            extensions: [new PathStyleExtension({ dash: true })],
          })
        )
      );

      // ----------
      // Overlays
      // ----------

      const renderSubLayers = (props) => {
        const {
          bbox: { west, south, east, north },
        } = props.tile;

        return new BitmapLayer({
          id: nanoid(),
          image: props.data,
          opacity: 0.3,
          bounds: [west, south, east, north],
        });
      };

      async function fetchData(url) {
        return await axios.get(url);
      }

      Object.keys(overlayState).forEach((overlayId) => {
        const overlay = overlayState[overlayId];
        if (!overlay.active) {
          return;
        }
        switch (overlayState[overlayId].layerType) {
          case "WMSOverlay":
            let url = "";
            switch (overlay.serverType) {
              case "geoserver":
                url =
                  `${overlay.baseUrl}${overlay.workspaceId}/wms` +
                  "?" +
                  Object.keys(overlay.queryParams)
                    .map((key) => key + "=" + overlay.queryParams[key])
                    .join("&");
                break;
              case "mapserver":
              default:
                url =
                  `${overlay.baseUrl}?` +
                  Object.keys(overlay.queryParams)

                    .map((key) => key + "=" + overlay.queryParams[key])
                    .join("&");
                break;
            }

            newLayers.push(
              new WMSLayer({
                id: overlay.id,
                data: url,
                serviceType: "template",
                layers: [overlay.layerId],
                opacity: overlay.opacity,
              })
            );

            break;
          case "TileLayer":
            if (overlayState[overlayId].active) {
              newLayers.push(
                new TileLayer({
                  id: overlayId,
                  getTileData: (tile) => {
                    let url_bbox;
                    // apply transform on some layers
                    if (overlayState[overlayId].transform) {
                      const originalProj =
                        overlayState[overlayId].transformConfig
                          .originalProjection;
                      const newProj =
                        overlayState[overlayId].transformConfig.newProjection;
                      const [west, south] = proj4(originalProj, newProj, [
                        tile.bbox.west,
                        tile.bbox.south,
                      ]);
                      const [east, north] = proj4(originalProj, newProj, [
                        tile.bbox.east,
                        tile.bbox.north,
                      ]);
                      url_bbox = `&bbox=${west},${south},${east},${north}`;
                    }
                    // else standard logic, no transform
                    else {
                      const { west, north, east, south } = tile.bbox;
                      url_bbox = `&bbox=${west},${south},${east},${north}`;
                    }
                    return (
                      overlayState[overlayId].baseUrl +
                      overlayState[overlayId].urlParams +
                      url_bbox
                    );
                  },
                  renderSubLayers,
                })
              );
            }
            break;

          case "GeoJsonLayer":
            if (overlayState[overlayId].active) {
              // fetch data if it doesn't yet exist
              if (!overlayData[overlayId]) {
                dispatch(
                  overlayActions.setIsFetching({ overlayId, isFetching: true })
                );
                fetchData(overlayState[overlayId].baseUrl).then((response) => {
                  dispatch(
                    overlayActions.setIsFetching({
                      overlayId,
                      isFetching: false,
                    })
                  );
                  setOverlayData((prevState) => {
                    return { ...prevState, [overlayId]: response.data };
                  });
                });
              }

              newLayers.push(
                new GeoJsonLayer({
                  id: overlayId,
                  data: overlayData[overlayId],
                  ...overlayState[overlayId].layerConfig,
                  // nonstandard case: map feature property values to colors
                  getFillColor: (d) => {
                    if (
                      overlayState[overlayId].fillColorMap &&
                      overlayState[overlayId].fillColorProperty
                    ) {
                      const featureValue =
                        d.properties[overlayState[overlayId].fillColorProperty];

                      const colorProperty = overlayState[
                        overlayId
                      ].fillColorMap?.find((v) => v.value === featureValue);

                      if (colorProperty) {
                        return colorProperty.color;
                      } else {
                        return DEFAULT_FILL_COLOR;
                      }
                    }
                    return DEFAULT_FILL_COLOR;
                  },
                  ...overlayState[overlayId].layerConfig,
                  extensions: overlayState[overlayId].pathExtension
                    ? [new PathStyleExtension({ dash: true })]
                    : [],
                })
              );
            }
            break;

          default:
            break;
        }
      });

      // Add async layers to new layers
      resolvedLayers.forEach((layer) => {
        if (Array.isArray(layer)) {
          newLayers.push(...layer);
        } else {
          newLayers.push(layer);
        }
      });

      // If user is selecting AOI on map, add AOI overlay polygons last
      // to render them over all other layers
      if (aoiState.overlay) {
        newLayers.push(
          new GeoJsonLayer({
            id: "aoiOverlay",
            data: dataState[
              aoiCategories.find((aoiCat) => aoiCat.id === aoiState.overlay)
                .data
            ].data,
            filled: true,
            getFillColor: [160, 160, 180, 200],
            getLineColor: [160, 160, 180, 255],
            getLineWidth: 3,
            lineWidthMinPixels: 3,
            lineWidthUnits: "pixels",
            onClick: handleOverlayPolygonClick,
            onHover: (e) => handleOverlayPolygonHover(e),
            pickable: true,
            autoHighlight: true,
            highlightColor: [235, 222, 52, 200],
          })
        );
      }

      // Polygon tool layer
      if (polygonLayerVisibility) {
        newLayers.push(
          new EditableGeoJsonLayer({
            data: polygonState.features,
            mode,
            modeConfig: {
              preventOverlappingLines: true,
            },
            selectedFeatureIndexes: polygonState.selectedFeatureIndexes,
            onEdit: ({ updatedData }) => {
              dispatch(polygonActions.setFeatures(updatedData));
            },
          })
        );
      }

      setLayers(newLayers);
    });
  }, [
    JSON.stringify(aoiState),
    JSON.stringify(mapState),
    JSON.stringify(siteState.filteredData),
    JSON.stringify(mode),
    JSON.stringify(overlayState),
    JSON.stringify(activeLayers),
    JSON.stringify(colorValues),
    JSON.stringify(activeFilters),
    polygonLayerVisibility,
    JSON.stringify(polygonState.features),
    JSON.stringify(polygonState.selectedFeatureIndexes),
    staticLayersFetched,
    derivedDataState,
  ]);

  return (
    <>
      <ChangeView />
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          zIndex: 3,
          top: "10px",
          right: "50px",
          position: "absolute",
          width: 0,
        }}
      >
        <Home />
        <Basemap />
        {/* <OverlaySelection /> */}
        <PolygonTool
          features={polygonState.features}
          onChange={() => setPolygonToolActive((prevState) => !prevState)}
          polygonLayerVisibility={polygonLayerVisibility}
          selectedFeatureIndexes={polygonState.selectedFeatureIndexes}
          setMode={setMode}
          setPolygonLayerVisibility={setPolygonLayerVisibility}
          toolActive={polygonToolActive}
        />
        <DataStepperButton />
      </Box>
      <DeckGL
        viewState={localViewState}
        onViewStateChange={(e) => setLocalViewState(e.viewState)}
        controller={{
          inertia: true,
          scrollZoom: { speed: 0.03, smooth: true },
          doubleClickZoom: mode === ViewMode,
        }}
        layers={layers}
        ContextProvider={MapContext.Provider}
        onClick={(info, event) => handleMapEvent(info, event)}
        onHover={(info, event) => handleMapEvent(info, event)}
      >
        {tooltipInfo?.lat && tooltipInfo?.lon && (
          <Popup
            longitude={tooltipInfo.lon}
            latitude={tooltipInfo.lat}
            anchor="bottom"
          >
            <Tooltip tooltipInfo={tooltipInfo} />
          </Popup>
        )}
        {mapState.basemap.type === "mapbox" && (
          <>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                zIndex: 3,
                bottom: "15px",
                right: "50%",
                position: "absolute",
                width: 0,
              }}
            >
              <ScaleControl />
            </Box>
            <StaticMap
              // key required to prevent basemap rerender
              //  see https://github.com/visgl/deck.gl/issues/4419
              key="basemap"
              mapStyle={mapState.basemap.src}
              mapboxApiAccessToken={process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}
            />
          </>
        )}
      </DeckGL>
    </>
  );
}

export default Map;
